<template>
    <div class="dual-list-selector">
        <div class="available-items">
            <div class="h4 section-title">
                {{ availableItemsLabel ?? __('messages.available_items') }}
            </div>
            <div
                v-for="group in groups"
                :key="group.name"
                class="mb-3"
            >
                <div
                    class="h4 group-name"
                    @click="toggleGroup(group.name)"
                >
                    <DIcon :name="collapsedGroups[group.name] ? 'arrow-up' : 'arrow-down'" /> {{ group.name }}
                </div>
                <ul v-if="collapsedGroups[group.name]">
                    <li
                        v-for="item in availableItems(group)"
                        :key="item.value"
                        class="cursor-pointer item-row"
                        @click="select(item)"
                    >
                        <div>
                            <div>{{ item.label }}</div>
                            <div
                                v-if="item.description"
                                class="text-muted fst-italic item-description"
                            >
                                {{ item.description }}
                            </div>
                        </div>
                        <DIcon
                            name="arrow-right"
                            :size="28"
                            class="move-arrow"
                        />
                    </li>
                </ul>
            </div>
        </div>
        <div class="selected-items">
            <div class="h4 section-title">
                {{ selectedItemsLabel ?? __('messages.selected_items') }}
            </div>
            <ul>
                <li
                    v-for="item in modelValue"
                    :key="item.value"
                    class="cursor-pointer item-row"
                    @click="deselect(item)"
                >
                    <DIcon
                        name="arrow-left"
                        :size="28"
                        class="move-arrow"
                    />
                    <div>{{ item.label }}</div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    groups: {
        // [{ name: 'Group Label', items: [{ value, label, description? }, ...] }, ...]
        type: Array,
        default: () => []
    },
    modelValue: {
        // [{ value, label }, ...]
        type: Array,
        default: () => []
    },
    availableItemsLabel: {
        type: String,
        default: null
    },
    selectedItemsLabel: {
        type: String,
        default: null
    }
})
const emit = defineEmits(['update:modelValue'])

const collapsedGroups = ref(Object.fromEntries(props.groups.map(group => [group.name, false])))

function toggleGroup (name) {
    collapsedGroups.value[name] = !collapsedGroups.value[name]
}
function availableItems (group) {
    return group.items.filter(item => !props.modelValue.some(selected => selected.value === item.value))
}
function select (item) {
    emit('update:modelValue', [...props.modelValue, item])
}
function deselect (item) {
    emit('update:modelValue', props.modelValue.filter(selected => selected.value !== item.value))
}
</script>

<style scoped>
.dual-list-selector {
    display: flex;
    gap: 1rem;
}

.available-items,
.selected-items {
    flex: 1;
    margin: 1rem;
    border: 2px solid var(--colorAppBorder);
    padding: 1rem;
}

.section-title {
    padding: 0.5rem 0;
}

.group-name,
.cursor-pointer {
    cursor: pointer;
}

.item-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 1.1em;
    border: 1px solid var(--colorAppBorder);
    margin: 0.25rem 0;
    padding: 0.25rem 0.5rem;
}

.selected-items .item-row {
    justify-content: flex-start;
}

.item-description {
    font-size: 0.8em;
}

.move-arrow {
    color: #ffc107;
    flex-shrink: 0;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 2px;
}
</style>
